import React, { useEffect, useMemo, useRef, useState } from 'react';
import { nlpQuery } from '../../utils/expenseApi';

const STORAGE_KEY = 'epe_chat_state_v1';

const WELCOME = {
  id: 'welcome',
  sender: 'ai',
  text: "Hi, I'm your Expense Assistant. Ask me anything about your claims, policy rules, approvals, and spend analytics.",
};

const QUICK_PROMPTS = [
  'What are my total expenses this month?',
  'Which of my claims are pending approval?',
  'Show me the top expense categories by spend.',
  'How many claims were rejected in the last 30 days?',
];

function nowLabel() {
  return new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function buildId() {
  return `epe-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

function makeTitle(q) {
  const t = (q || '').trim();
  return t.length > 32 ? `${t.slice(0, 32)}…` : t || 'New chat';
}

function buildSession(title = 'New chat') {
  return { id: buildId(), title, updatedAt: Date.now(), messages: [WELCOME] };
}

function getInitial() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      const s = buildSession('Chat 1');
      return { open: false, expanded: false, fullScreen: false, activeId: s.id, sessions: [s] };
    }
    const p = JSON.parse(raw);
    const sessions = Array.isArray(p.sessions) && p.sessions.length > 0
      ? p.sessions : [buildSession('Chat 1')];
    const activeId = sessions.some(s => s.id === p.activeId) ? p.activeId : sessions[0].id;
    return {
      open:       !!p.open,
      expanded:   !!p.expanded,
      fullScreen: !!p.fullScreen,
      activeId,
      sessions,
    };
  } catch {
    const s = buildSession('Chat 1');
    return { open: false, expanded: false, fullScreen: false, activeId: s.id, sessions: [s] };
  }
}

export default function ExpenseQuery() {
  const init = useMemo(() => getInitial(), []);
  const [open,        setOpen]        = useState(init.open);
  const [expanded,    setExpanded]    = useState(init.expanded);
  const [fullScreen,  setFullScreen]  = useState(init.fullScreen);
  const [sessions,    setSessions]    = useState(init.sessions);
  const [activeId,    setActiveId]    = useState(init.activeId);
  const [input,       setInput]       = useState('');
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState('');
  const [editingId,   setEditingId]   = useState(null);
  const [renameVal,   setRenameVal]   = useState('');

  const bottomRef   = useRef(null);
  const textareaRef = useRef(null);

  const active   = useMemo(() => sessions.find(s => s.id === activeId) || sessions[0], [sessions, activeId]);
  const messages = active?.messages || [WELCOME];
  const canSend  = input.trim().length > 0 && !loading;

  // Persist
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify({ open, expanded, fullScreen, activeId, sessions }));
  }, [open, expanded, fullScreen, activeId, sessions]);

  // Escape exits fullscreen
  useEffect(() => {
    if (!fullScreen) return;
    const fn = (e) => { if (e.key === 'Escape') setFullScreen(false); };
    window.addEventListener('keydown', fn);
    return () => window.removeEventListener('keydown', fn);
  }, [fullScreen]);

  useEffect(() => {
    document.body.style.overflow = fullScreen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [fullScreen]);

  // Auto-scroll
  useEffect(() => {
    if (open) bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [open, messages, loading]);

  // Focus textarea when opening
  useEffect(() => {
    if (open) setTimeout(() => textareaRef.current?.focus(), 100);
  }, [open]);

  const patchActive = (patch) => {
    setSessions(prev => prev.map(s =>
      s.id === activeId ? { ...s, ...patch, updatedAt: Date.now() } : s
    ));
  };

  const sendMessage = async (q) => {
    const clean = (q || input).trim();
    if (!clean || loading) return;

    const userMsg   = { id: `u-${Date.now()}`, sender: 'user', text: clean, time: nowLabel() };
    const nextTitle = messages.length <= 1 ? makeTitle(clean) : active.title;
    const nextMsgs  = [...messages, userMsg];

    patchActive({ title: nextTitle, messages: nextMsgs });
    setInput('');
    setError('');
    setLoading(true);

    try {
      const res = await nlpQuery(clean);
      const aiMsg = { id: `a-${Date.now()}`, sender: 'ai', text: res.answer || 'No response returned.', time: nowLabel() };
      patchActive({ messages: [...nextMsgs, aiMsg] });
    } catch (err) {
      const status = err.response?.status;
      setError(
        status === 503
          ? 'AI service temporarily unavailable. Try again in a moment.'
          : err.response?.data?.message || 'Failed to get a response. Please try again.'
      );
    }
    setLoading(false);
    setTimeout(() => textareaRef.current?.focus(), 50);
  };

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); }
  };

  const newChat = () => {
    const s = buildSession(`Chat ${sessions.length + 1}`);
    setSessions(prev => [s, ...prev]);
    setActiveId(s.id);
    setInput('');
    setError('');
  };

  const clearChat = () => {
    patchActive({ title: 'New chat', messages: [WELCOME] });
    setError('');
  };

  const deleteSession = (id) => {
    setSessions(prev => {
      const rest = prev.filter(s => s.id !== id);
      if (rest.length === 0) {
        const fb = buildSession('Chat 1');
        setActiveId(fb.id);
        return [fb];
      }
      if (activeId === id) setActiveId(rest[0].id);
      return rest;
    });
    if (editingId === id) { setEditingId(null); setRenameVal(''); }
  };

  const saveRename = (id) => {
    const t = renameVal.trim();
    if (!t) return;
    setSessions(prev => prev.map(s => s.id === id ? { ...s, title: t } : s));
    setEditingId(null); setRenameVal('');
  };

  const sorted = useMemo(
    () => [...sessions].sort((a, b) => (b.updatedAt || 0) - (a.updatedAt || 0)),
    [sessions]
  );

  const showSidebar = expanded || fullScreen;

  return (
    <div className={`epe-widget-container ${open ? 'is-open' : ''} ${fullScreen ? 'fullscreen' : ''}`}>
      {/* Toggle button */}
      {!open && (
        <button
          type="button"
          className="epe-widget-toggle"
          onClick={() => setOpen(true)}
          aria-label="Open Expense Assistant"
        >
          Ask AI
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <section className={`epe-widget-panel ${expanded ? 'expanded' : ''}`} aria-label="Expense Chat Assistant">

          {/* Header */}
          <header className="epe-widget-header">
            <div className="epe-widget-title">
              <h3>Expense Assistant</h3>
              <p>{loading ? 'Analyzing…' : 'Powered by AI · Live data access'}</p>
            </div>
            <div className="epe-widget-actions">
              <button type="button" onClick={newChat}>New</button>
              <button type="button" onClick={clearChat}>Clear</button>
              <button type="button" onClick={() => setExpanded(p => !p)}>{expanded ? 'Min' : 'Max'}</button>
              {fullScreen
                ? <button type="button" onClick={() => setFullScreen(false)}>Exit</button>
                : <button type="button" onClick={() => { setExpanded(true); setFullScreen(true); }}>Full</button>
              }
              <button type="button" onClick={() => { setOpen(false); setFullScreen(false); }}>Close</button>
            </div>
          </header>

          {/* Body */}
          <div className="epe-widget-body">

            {/* Sessions sidebar */}
            {showSidebar && (
              <aside className="epe-widget-sessions" aria-label="Chat sessions">
                <h4>Sessions</h4>
                <div className="epe-widget-session-list">
                  {sorted.map(s => (
                    <div key={s.id} className={`epe-widget-session-item ${s.id === activeId ? 'active' : ''}`}>
                      {editingId === s.id ? (
                        <div className="epe-widget-session-rename" onClick={e => e.stopPropagation()}>
                          <input
                            value={renameVal}
                            onChange={e => setRenameVal(e.target.value)}
                            onKeyDown={e => {
                              if (e.key === 'Enter') { e.preventDefault(); saveRename(s.id); }
                              if (e.key === 'Escape') { setEditingId(null); setRenameVal(''); }
                            }}
                            autoFocus
                          />
                          <div className="epe-widget-session-rename-actions">
                            <button type="button" onClick={() => saveRename(s.id)}>Save</button>
                            <button type="button" onClick={() => { setEditingId(null); setRenameVal(''); }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <button
                            type="button"
                            className="epe-widget-session-main"
                            onClick={() => { setActiveId(s.id); setError(''); }}
                          >
                            <strong>{s.title}</strong>
                            <span>{new Date(s.updatedAt || Date.now()).toLocaleString([], { month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit' })}</span>
                          </button>
                          <div className="epe-widget-session-item-actions">
                            <button type="button" onClick={() => { setEditingId(s.id); setRenameVal(s.title); }}>Edit</button>
                            <button type="button" onClick={() => deleteSession(s.id)}>Del</button>
                          </div>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </aside>
            )}

            {/* Main */}
            <div className="epe-widget-main">
              <div className="epe-widget-messages">
                {messages.length <= 1 && (
                  <div className="epe-widget-quick-prompts">
                    {QUICK_PROMPTS.map(p => (
                      <button key={p} type="button" onClick={() => sendMessage(p)}>{p}</button>
                    ))}
                  </div>
                )}

                {messages.map(msg => (
                  <article key={msg.id} className={`epe-widget-message ${msg.sender === 'user' ? 'user' : 'ai'}`}>
                    <div className="epe-widget-bubble">
                      <p>{msg.text}</p>
                      {msg.time && <small className="epe-widget-time">{msg.time}</small>}
                    </div>
                  </article>
                ))}

                {loading && (
                  <div className="epe-widget-typing">
                    <span /><span /><span />
                  </div>
                )}

                <div ref={bottomRef} />
              </div>

              {error && <p className="epe-widget-error">{error}</p>}

              <form className="epe-widget-input" onSubmit={e => { e.preventDefault(); sendMessage(); }}>
                <textarea
                  ref={textareaRef}
                  value={input}
                  onChange={e => { setInput(e.target.value); setError(''); }}
                  onKeyDown={handleKey}
                  rows={2}
                  placeholder="Ask about claims, policy rules, spend…"
                  disabled={loading}
                />
                <button type="submit" disabled={!canSend}>
                  {loading ? 'Sending…' : 'Send'}
                </button>
              </form>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
